import { useState, useEffect } from "react";
import axios from "axios";
import { Home, Trophy, Crown, Users, ArrowLeft, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZODIAC_EMOJIS = {
  Aries: "♈", Tauro: "♉", Geminis: "♊", Cancer: "♋",
  Leo: "♌", Virgo: "♍", Libra: "♎", Escorpio: "♏",
  Sagitario: "♐", Capricornio: "♑", Acuario: "♒", Piscis: "♓"
};

const HOUSE_COLORS = {
  Aries: "from-red-500/20 to-red-900/10 border-red-500/30",
  Tauro: "from-green-500/20 to-green-900/10 border-green-500/30",
  Geminis: "from-yellow-500/20 to-yellow-900/10 border-yellow-500/30",
  Cancer: "from-slate-400/20 to-slate-700/10 border-slate-400/30",
  Leo: "from-orange-500/20 to-orange-900/10 border-orange-500/30",
  Virgo: "from-emerald-500/20 to-emerald-900/10 border-emerald-500/30",
  Libra: "from-pink-500/20 to-pink-900/10 border-pink-500/30",
  Escorpio: "from-purple-500/20 to-purple-900/10 border-purple-500/30",
  Sagitario: "from-indigo-500/20 to-indigo-900/10 border-indigo-500/30",
  Capricornio: "from-stone-500/20 to-stone-900/10 border-stone-500/30",
  Acuario: "from-cyan-500/20 to-cyan-900/10 border-cyan-500/30",
  Piscis: "from-teal-500/20 to-teal-900/10 border-teal-500/30"
};

const HouseCard = ({ house, isWinner }) => {
  const colors = HOUSE_COLORS[house.house] || "from-slate-500/20 to-slate-900/10 border-slate-500/30";
  const emoji = house.emoji || ZODIAC_EMOJIS[house.house] || "";

  return (
    <div
      className={`relative p-4 rounded-xl bg-gradient-to-br ${colors} border backdrop-blur-sm transition-all hover:scale-[1.02]`}
      data-testid={`house-card-${house.house}`}
    >
      {isWinner && (
        <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
          #1
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">{emoji}</span>
        <div>
          <h3 className="text-lg font-bold text-white" data-testid={`house-name-${house.house}`}>
            {house.house}
          </h3>
          <span className="text-xs text-slate-400">
            {house.members_count} {house.members_count === 1 ? "miembro" : "miembros"}
          </span>
        </div>
        <div className="ml-auto text-right">
          <div className="text-2xl font-bold text-white" data-testid={`house-points-${house.house}`}>
            {house.total_points}
          </div>
          <span className="text-xs text-slate-400">puntos</span>
        </div>
      </div>
      {house.members && house.members.length > 0 && (
        <div className="mt-2 space-y-1">
          {house.members.slice(0, 5).map((m, i) => (
            <div key={m.user_id} className="flex justify-between text-sm text-slate-300">
              <span>{m.first_name || m.username || `Usuario ${m.user_id}`}</span>
              <span className="text-slate-400">{m.points} pts</span>
            </div>
          ))}
          {house.members.length > 5 && (
            <div className="text-xs text-slate-500 text-center mt-1">
              +{house.members.length - 5} mas
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Houses = () => {
  const navigate = useNavigate();
  const [currentHouses, setCurrentHouses] = useState([]);
  const [historicalHouses, setHistoricalHouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHouses();
  }, []);

  const fetchHouses = async () => {
    try {
      setLoading(true);
      const [current, historical] = await Promise.all([
        axios.get(`${API}/houses/current`),
        axios.get(`${API}/houses/historical`)
      ]);
      setCurrentHouses(current.data);
      setHistoricalHouses(historical.data);
    } catch (error) {
      console.error("Error fetching houses:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupedHistory = historicalHouses.reduce((acc, item) => {
    if (!acc[item.month]) acc[item.month] = [];
    acc[item.month].push(item);
    return acc;
  }, {});

  return (
    <div className="page-container">
      <div className="page-header">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-4 text-purple-300 hover:text-purple-200"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>
        <h1 className="page-title" data-testid="houses-title">
          <Home className="inline-block w-10 h-10 mb-1" />
          {" "}Casas del Horoscopo
        </h1>
        <p className="page-subtitle" data-testid="houses-subtitle">
          Unete a una casa y compite con tu equipo
        </p>
      </div>

      <Tabs defaultValue="current" className="w-full">
        <TabsList
          className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-slate-800/50 p-1 rounded-lg"
          data-testid="houses-tabs"
        >
          <TabsTrigger
            value="current"
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-md transition-all"
            data-testid="tab-houses-current"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Mes Actual
          </TabsTrigger>
          <TabsTrigger
            value="historical"
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-md transition-all"
            data-testid="tab-houses-historical"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Historico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" data-testid="houses-current-content">
          {loading ? (
            <div className="text-center py-8 text-slate-400" data-testid="houses-loading">
              Cargando casas...
            </div>
          ) : currentHouses.length === 0 ? (
            <Card className="glass-card border-slate-700">
              <CardContent className="text-center py-12 text-slate-400">
                <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aun no hay casas con miembros.</p>
                <p className="text-sm mt-2">Usa <code>/casa [signo]</code> en el tema Horoscopo de Telegram</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="houses-grid">
              {currentHouses.map((house) => (
                <HouseCard key={house.house} house={house} isWinner={house.rank === 1} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historical" data-testid="houses-historical-content">
          {Object.keys(groupedHistory).length === 0 ? (
            <Card className="glass-card border-slate-700">
              <CardContent className="text-center py-12 text-slate-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aun no hay historial de casas.</p>
                <p className="text-sm mt-2">El historial se generara al final de cada mes.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedHistory).map(([month, houses]) => (
                <Card key={month} className="glass-card border-slate-700">
                  <CardHeader>
                    <CardTitle className="ranking-title text-lg">
                      <Crown className="w-5 h-5 text-yellow-400" />
                      {month}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {houses.map((h) => (
                        <div
                          key={h.house}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            h.winner ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-slate-800/30"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{h.emoji || ZODIAC_EMOJIS[h.house]}</span>
                            <span className="font-semibold text-white">{h.house}</span>
                            {h.winner && <span className="text-yellow-400 text-xs font-bold">GANADORA</span>}
                          </div>
                          <div className="text-right">
                            <span className="text-white font-bold">{h.total_points} pts</span>
                            <span className="text-slate-400 text-xs ml-2">({h.members_count} miembros)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="glass-card mt-8" data-testid="houses-info-card">
        <h3 className="text-xl font-semibold mb-4 text-purple-300">
          <Home className="w-5 h-5 inline-block mr-2" />
          Como funcionan las Casas
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-slate-300 text-sm">
          <div>
            <h4 className="font-semibold text-purple-200 mb-2">Comandos:</h4>
            <ul className="space-y-1">
              <li>- <code>/casa [signo]</code> - Unirte a una casa</li>
              <li>- <code>/casas</code> - Ver ranking de casas</li>
              <li>- <code>/micasa</code> - Ver info de tu casa</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-purple-200 mb-2">Reglas:</h4>
            <ul className="space-y-1">
              <li>- Tus puntos de participacion suman a tu casa</li>
              <li>- Puedes cambiar de casa solo 1 vez</li>
              <li>- La casa ganadora recibe +10 pts participacion</li>
              <li>- Los puntos se resetean cada mes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Houses;
